import { ErrorCode, type Accept, type FileRejection } from "react-dropzone";

/** Same rules as RequestService: space-separated, optional leading dot. */
export function parseTaxonomyAllowedExtensions(allowed: string | null | undefined): string[] {
    if (!allowed?.trim()) return [];
    return allowed
        .split(/\s+/)
        .map((s) => s.replace(/^\./, "").toLowerCase())
        .filter(Boolean);
}

/** e.g. "pdf jpg" → "PDF, JPG" */
export function formatAllowedExtensionsLabel(allowed: string | null | undefined): string | null {
    const list = parseTaxonomyAllowedExtensions(allowed);
    if (!list.length) return null;
    return list.map((e) => e.toUpperCase()).join(", ");
}

/** SI megabytes (1 MB = 1_000_000 bytes), one decimal — e.g. 4194304 → "4.2MB" */
export function formatMaxFileSizeMb(bytes: number | null | undefined): string | null {
    if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
    const mb = bytes / 1_000_000;
    return `${mb.toFixed(1)}MB`;
}

export function getTaxonomyUploadConstraintsHint(allowed: string | null | undefined, maxBytes: number | null | undefined): string {
    const extLabel = formatAllowedExtensionsLabel(allowed);
    const maxLabel = formatMaxFileSizeMb(maxBytes ?? null);
    if (extLabel && maxLabel) return `${extLabel} (max ${maxLabel})`;
    if (extLabel) return extLabel;
    if (maxLabel) return `(max ${maxLabel})`;
    return "";
}

type TaxonomyUploadRejectionOptions = {
    taxonomyName: string | null | undefined;
    allowedExtensions: string | null | undefined;
    maxFileSize: number | null | undefined;
    maxNumberOfFiles?: number | null;
};

function taxonomyUploadLabel(name: string | null | undefined): string {
    const trimmed = name?.trim();
    return trimmed ? trimmed : "túto prílohu";
}

/** User-facing text when react-dropzone rejects files (size, type, count). */
export function getTaxonomyUploadRejectionMessage(
    rejections: FileRejection[],
    options: TaxonomyUploadRejectionOptions,
): string | null {
    if (!rejections.length) return null;

    const taxonomyLabel = taxonomyUploadLabel(options.taxonomyName);
    const codes = new Set(rejections.flatMap((rejection) => rejection.errors.map((error) => error.code)));

    if (codes.has(ErrorCode.FileTooLarge)) {
        const maxLabel = formatMaxFileSizeMb(options.maxFileSize);
        if (maxLabel) {
            return `Súbor nezodpovedá inštrukciám pre „${taxonomyLabel}“. Maximálna veľkosť súboru je ${maxLabel}.`;
        }
        return `Súbor nezodpovedá inštrukciám pre „${taxonomyLabel}“.`;
    }

    if (codes.has(ErrorCode.FileInvalidType)) {
        const extLabel = formatAllowedExtensionsLabel(options.allowedExtensions);
        if (extLabel) {
            return `Súbor nezodpovedá inštrukciám pre „${taxonomyLabel}“. Povolené formáty: ${extLabel}.`;
        }
        return `Súbor nezodpovedá inštrukciám pre „${taxonomyLabel}“.`;
    }

    if (codes.has(ErrorCode.TooManyFiles)) {
        const maxFiles = options.maxNumberOfFiles;
        if (maxFiles != null && Number.isFinite(maxFiles) && maxFiles > 0) {
            return `Bol prekročený maximálny počet súborov pre „${taxonomyLabel}“ (${maxFiles}).`;
        }
        return `Bol prekročený maximálny počet súborov pre „${taxonomyLabel}“.`;
    }

    return `Súbor nezodpovedá inštrukciám pre „${taxonomyLabel}“.`;
}

const EXT_TO_MIME: Record<string, string> = {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    tif: "image/tiff",
    tiff: "image/tiff",
    txt: "text/plain",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    csv: "text/csv",
};

/** For react-dropzone `accept`; unknown extensions use application/octet-stream. */
export function buildDropzoneAccept(extensions: string[]): Accept | undefined {
    if (!extensions.length) return undefined;
    const accept: Accept = {};
    for (const ext of extensions) {
        const mime = EXT_TO_MIME[ext] ?? "application/octet-stream";
        const arr = [...(accept[mime] ?? [])];
        arr.push(`.${ext}`);
        accept[mime] = arr;
    }
    return accept;
}
